function CuratorMark({ size = 50, inverse = false }) {
  // Layered Bauhaus composition:
  //  L1 — A ring + shaft + teeth that reads as BOTH a rising sun AND a key bow
  //  L2 — Architectural frame (the property)
  //  L3 — Pitched roof
  //  L4 — Window with accent
  //  L5 — Elegant TPC monogram (refined typography, not chunky)
  const panel = inverse ? "#0B1215" : "#F8F9FA";
  const ink = inverse ? "#F8F9FA" : "#0B1215";
  const onInk = inverse ? "#0B1215" : "#F8F9FA";
  const accent = "#FF5A36";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="The Property Curator — TPC"
      style={{ display: "block" }}
    >
      <rect x="0" y="0" width="64" height="64" fill={panel} />

      {/* L1 — Sun/Key bow (ring), top-right.
          The ring reads as a SUN at first glance and a KEY BOW on second look. */}
      <circle cx="48" cy="16" r="11" fill={accent} />
      <circle cx="48" cy="16" r="5.5" fill={panel} />
      {/* Key shaft — extends down from the bow, partly hidden by the frame */}
      <rect x="46.4" y="22" width="3.2" height="14" fill={accent} />
      {/* Key teeth — two notches pointing right */}
      <rect x="49.6" y="28" width="3.5" height="2" fill={accent} />
      <rect x="49.6" y="32" width="2.5" height="2" fill={accent} />

      {/* L2 — Architectural frame */}
      <rect x="6" y="22" width="40" height="36" fill={ink} />

      {/* L3 — Pitched roof, slicing through the lower part of the sun/key */}
      <path d="M6 22 L26 6 L46 22 Z" fill={ink} />

      {/* L4 — Window (off-white) with orange accent dot — second-look detail */}
      <rect x="33" y="40" width="9" height="10" fill={onInk} />
      <line x1="37.5" y1="40" x2="37.5" y2="50" stroke={ink} strokeWidth="0.8" />
      <line x1="33" y1="45" x2="42" y2="45" stroke={ink} strokeWidth="0.8" />
      <rect x="36.5" y="44.2" width="2" height="1.6" fill={accent} />

      {/* L5 — TPC monogram, refined & elegant.
          Each letter is centred on the SAME vertical axis using text-anchor="middle"
          so the round C, the symmetric T and the right-heavy P all sit on a perfect
          shared centreline (OCD-friendly). */}
      <g
        fill={onInk}
        textAnchor="middle"
        style={{
          fontFamily: "'Cabinet Grotesk', sans-serif",
          fontWeight: 700,
          letterSpacing: "-0.04em",
        }}
      >
        <text x="17" y="33.5" fontSize="10">T</text>
        <text x="17" y="44.5" fontSize="10">P</text>
        <text x="17" y="55.5" fontSize="10">C</text>
      </g>

      {/* Faint guide lines between the letters — editorial grid (centred on the stack) */}
      <g stroke={onInk} strokeWidth="0.4" opacity="0.25">
        <line x1="11" y1="35.5" x2="23" y2="35.5" />
        <line x1="11" y1="46.5" x2="23" y2="46.5" />
      </g>

      {/* Ground line */}
      <rect x="6" y="60" width="40" height="0.8" fill={ink} opacity="0.5" />
    </svg>
  );
}

export function Logo({ size = "md", inverse = false }) {
  const sizes = {
    sm: { text: "text-lg", icon: 36, sub: "0.5em" },
    md: { text: "text-2xl", icon: 52, sub: "0.55em" },
    lg: { text: "text-4xl sm:text-5xl", icon: 88, sub: "0.5em" },
  };
  const s = sizes[size];
  const ink = inverse ? "text-[#F8F9FA]" : "text-[#0B1215]";
  const sub = inverse ? "text-[#F8F9FA]/70" : "text-[#495057]";

  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <CuratorMark size={s.icon} inverse={inverse} />
      <div className="flex items-baseline gap-2">
        <div className={`font-display ${s.text} ${ink} leading-none flex items-baseline tracking-tighter`}>
          <span>The Property Curator</span>
          <span className="text-[#FF5A36]">.</span>
        </div>
        <span
          className={`font-mono-label ${sub} hidden md:inline`}
          style={{ fontSize: s.sub, letterSpacing: "0.16em", textTransform: "lowercase" }}
        >
          (warwickshire and surrounding areas)
        </span>
      </div>
    </div>
  );
}

export function LogoMark({ inverse = false }) {
  return (
    <div data-testid="brand-logo-mark" aria-label="The Property Curator">
      <CuratorMark size={56} inverse={inverse} />
    </div>
  );
}
